/**
 * The aggregate state, as the hub reports it: GET /api/state once, then SSE `patch`,
 * `event`, `audit` and `log` messages. Mock mode feeds the same functions.
 *
 * The reducers (`fromAggregate`, `applyPatchTo`, `addFeedEvents`, ...) are pure and
 * keep the identity of every collection and entity a patch did not touch, so
 * selectors with `useShallow` re-render only what changed.
 */
import { useMemo } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { shallow } from 'zustand/shallow';
import { PROJECT_PROTOCOL_EXT, pipelineKey, suggestionKey } from '@orgus/protocol';
import type {
  AggregateState,
  AuditEntry,
  Checkout,
  FeedEvent,
  Id,
  Job,
  LogChunk,
  Machine,
  Pipeline,
  Project,
  ProjectSuggestion,
  Session,
  SessionStatus,
  SseEventMap,
  StatePatch,
  Ts,
} from '@orgus/protocol';
import type { UiError } from './api.ts';
import { runningUnits } from './running.ts';
import { createScoper, feedInScope } from './scope.ts';
import type { ConnectionStatus } from './stream.ts';

export const FEED_LIMIT = 500;
export const AUDIT_LIMIT = 500;
export const HISTORY_LIMIT = 90;
export const LOG_LIMIT_CHARS = 512 * 1024;

export interface MachineSample {
  at: Ts;
  load: number;
  /** Used memory fraction 0..1. */
  mem: number;
}

export interface Entities {
  /** Hub time of the last state or patch applied. */
  at: Ts;
  machines: Record<Id, Machine>;
  projects: Record<Id, Project>;
  checkouts: Record<Id, Checkout>;
  sessions: Record<Id, Session>;
  /** Keyed by `pipelineKey(p)`. */
  pipelines: Record<string, Pipeline>;
  jobs: Record<Id, Job>;
  /** Keyed by `suggestionKey(s)`. */
  suggestions: Record<string, ProjectSuggestion>;
  /** Newest first, capped at FEED_LIMIT. */
  feed: FeedEvent[];
  /** Newest first, capped at AUDIT_LIMIT. */
  audit: AuditEntry[];
  /** Client-side samples of each machine's load and memory, oldest first, for sparklines. */
  machineHistory: Record<Id, MachineSample[]>;
  /** Feature-module state (state.ext), keyed by module; an SSE 'ext' event of type 'state' replaces a slice. */
  ext: Record<string, unknown>;
}

export function emptyEntities(): Entities {
  return {
    at: 0,
    machines: {},
    projects: {},
    checkouts: {},
    sessions: {},
    pipelines: {},
    jobs: {},
    suggestions: {},
    feed: [],
    audit: [],
    machineHistory: {},
    ext: {},
  };
}

function byId<T extends { id: Id }>(list: T[]): Record<Id, T> {
  const out: Record<Id, T> = {};
  for (const x of list) out[x.id] = x;
  return out;
}

function byKey<T>(list: T[], key: (x: T) => string): Record<string, T> {
  const out: Record<string, T> = {};
  for (const x of list) out[key(x)] = x;
  return out;
}

function sample(m: Machine): MachineSample {
  return { at: m.lastSeen, load: m.load, mem: m.memTotal > 0 ? 1 - m.memFree / m.memTotal : 0 };
}

/** Append a sample when the machine reported something new; keeps the last HISTORY_LIMIT. */
export function pushSample(history: MachineSample[] | undefined, m: Machine): MachineSample[] {
  const prev = history ?? [];
  const last = prev[prev.length - 1];
  if (last && last.at >= m.lastSeen) return prev;
  const next = [...prev, sample(m)];
  return next.length > HISTORY_LIMIT ? next.slice(next.length - HISTORY_LIMIT) : next;
}

function newestFirst<T extends { at: Ts; id: Id }>(a: T, b: T): number {
  return b.at - a.at || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);
}

/** Merge events into a newest-first list, dropping duplicates by id, capped at `limit`. */
export function addFeedEvents<T extends { at: Ts; id: Id }>(list: T[], add: T[], limit: number): T[] {
  if (!add.length) return list;
  const seen = new Set(list.map((e) => e.id));
  const fresh = add.filter((e) => !seen.has(e.id));
  if (!fresh.length) return list;
  const merged = [...fresh, ...list].sort(newestFirst);
  return merged.length > limit ? merged.slice(0, limit) : merged;
}

/**
 * Full replacement from GET /api/state or the SSE `state` event. Machine history survives;
 * the hub's own recent samples (state.machineHistory) fill it on first load, so sparklines
 * have history the moment the page opens.
 */
export function fromAggregate(s: AggregateState, prev?: Entities): Entities {
  const machineHistory: Record<Id, MachineSample[]> = {};
  for (const m of s.machines) {
    const mine = prev?.machineHistory[m.id];
    const hub = s.machineHistory?.[m.id];
    const base = hub?.length && (!mine?.length || hub.length > mine.length) ? hub.slice(-HISTORY_LIMIT) : mine;
    machineHistory[m.id] = pushSample(base, m);
  }
  const ext = s.ext ?? prev?.ext ?? {};
  const declared = declaredNamesOf(ext);
  return {
    at: s.at,
    machines: byId(s.machines),
    projects: byId(s.projects.map((p) => namedProject(p, declared))),
    checkouts: byId(s.checkouts),
    sessions: byId(s.sessions),
    pipelines: byKey(s.pipelines, pipelineKey),
    jobs: byId(s.jobs),
    suggestions: byKey(s.suggestions ?? [], suggestionKey),
    feed: [...s.feed].sort(newestFirst).slice(0, FEED_LIMIT),
    audit: [...s.audit].sort(newestFirst).slice(0, AUDIT_LIMIT),
    machineHistory,
    ext,
  };
}

/**
 * Apply one SSE `ext` event. Type 'state' replaces that module's slice. Type 'changed' with a
 * `rev` (a module that keeps private data off the stream, such as auth) bumps the slice's rev,
 * so views of that module know to re-read. Other types are the module's own.
 */
export function applyExtTo(ext: Record<string, unknown>, e: SseEventMap['ext']): Record<string, unknown> {
  if (e.type === 'state') return { ...ext, [e.ext]: e.data };
  const rev = (e.data as { rev?: unknown } | null)?.rev;
  if (e.type === 'changed' && typeof rev === 'number') {
    const prev = ext[e.ext];
    return { ...ext, [e.ext]: { ...(prev && typeof prev === 'object' ? prev : {}), rev } };
  }
  return ext;
}

const idOf = <T extends { id: Id }>(x: T): string => x.id;

/** Names projects declare with `name:` in orgus.yml, from the project-protocol module's reports. */
export function declaredNamesOf(ext: Record<string, unknown>): Map<Id, string> {
  const out = new Map<Id, string>();
  const reports = (ext[PROJECT_PROTOCOL_EXT] as { projects?: { projectId?: Id; manifest?: { name?: unknown } | null }[] } | undefined)?.projects;
  for (const r of reports ?? []) {
    const name = r.manifest?.name;
    if (r.projectId && typeof name === 'string' && name.trim() && !out.has(r.projectId)) out.set(r.projectId, name);
  }
  return out;
}

/**
 * A project as the console shows it: the name orgus.yml declares exactly as written, else the
 * folder or remote name exactly as written too. A folder name is an identifier people type and
 * see in paths, branches and messages ("journey-app"), so every screen shows it one way (R-FN-8).
 */
function namedProject(p: Project, declared: Map<Id, string>): Project {
  const name = declared.get(p.id) ?? p.name;
  return name === p.name ? p : { ...p, name };
}

/** Give declared names to projects already held, once the project-protocol reports arrive. */
export function withDeclaredNames(e: Entities): Entities {
  const declared = declaredNamesOf(e.ext);
  let projects: Record<Id, Project> | null = null;
  for (const [id, name] of declared) {
    const p = e.projects[id];
    if (p && p.name !== name) (projects ??= { ...e.projects })[id] = { ...p, name };
  }
  return projects ? { ...e, projects } : e;
}

type PatchColl = 'machines' | 'projects' | 'checkouts' | 'sessions' | 'pipelines' | 'jobs' | 'suggestions';
const PATCH_KEYS: { [K in PatchColl]: (x: Entities[K][string]) => string } = {
  machines: idOf,
  projects: idOf,
  checkouts: idOf,
  sessions: idOf,
  pipelines: pipelineKey,
  jobs: idOf,
  suggestions: suggestionKey,
};
const PATCH_COLLS = Object.keys(PATCH_KEYS) as PatchColl[];

/** Apply one SSE `patch`. Collections the patch does not mention keep their identity. */
export function applyPatchTo(state: Entities, patch: StatePatch): Entities {
  return applyPatchesTo(state, [patch]);
}

/**
 * Apply many patches at once, in order, with one copy of each collection they touch (not one
 * per patch): a busy fleet sends many patches between two frames, and a 10,000-session
 * collection is too big to copy for each. Untouched collections keep their identity.
 */
export function applyPatchesTo(state: Entities, patches: readonly StatePatch[]): Entities {
  if (!patches.length) return state;
  const copies: Partial<Record<PatchColl, Record<string, unknown>>> = {};
  const own = (c: PatchColl) => (copies[c] ??= { ...state[c] });
  let machineHistory = state.machineHistory;
  let historyCopied = false;
  let at = state.at;
  let declared: Map<Id, string> | null = null;
  for (const p of patches) {
    at = Math.max(at, p.at);
    const u = p.upserts ?? {};
    const r = p.removes ?? {};
    for (const c of PATCH_COLLS) {
      const ups = u[c] as Entities[typeof c][string][] | undefined;
      const rms = r[c];
      if (ups?.length) {
        const rec = own(c);
        const key = PATCH_KEYS[c] as (x: unknown) => string;
        for (const x of ups) rec[key(x)] = c === 'projects' ? namedProject(x as Project, (declared ??= declaredNamesOf(state.ext))) : x;
      }
      if (rms?.length) {
        const rec = own(c);
        for (const k of rms) delete rec[k];
      }
    }
    if (u.machines?.length || r.machines?.length) {
      if (!historyCopied) machineHistory = { ...machineHistory };
      historyCopied = true;
      for (const m of u.machines ?? []) machineHistory[m.id] = pushSample(machineHistory[m.id], m);
      for (const id of r.machines ?? []) delete machineHistory[id];
    }
  }
  return { ...state, ...(copies as Partial<Entities>), at, machineHistory };
}

// ---------------------------------------------------------------------------
// The store
// ---------------------------------------------------------------------------

export type LoadPhase = 'loading' | 'ready' | 'error';
export type DataMode = 'live' | 'mock';

export interface LogBuffer {
  text: string;
  ended: boolean;
  updatedAt: Ts;
}

/** Stream messages gathered between two renders. */
export interface LiveBatch {
  patches: StatePatch[];
  events: FeedEvent[];
  audit: AuditEntry[];
  /**
   * Module (ext) events, applied after the patches in the same update: the hub sends them
   * after the patch in each flush, so a module state that names a session never shows up
   * before the session itself.
   */
  ext: SseEventMap['ext'][];
}

export interface OrgusStore extends Entities {
  /** loading until the first state arrives; error when that first load failed. */
  phase: LoadPhase;
  loadError: UiError | null;
  connection: ConnectionStatus;
  mode: DataMode;
  /** Wall-clock time of the last message from the hub (or simulator). */
  lastMessageAt: Ts | null;
  logs: Record<string, LogBuffer>;
  /** Log streams this tab opened and still shows. Chunks for any other stream are not kept. */
  openLogs: Record<string, true>;
  /** Chunks that arrived before their stream's open request returned, briefly held. */
  earlyLogs: Record<string, LogBuffer>;
  /** True when the audit log on the hub holds entries older than `audit`. */
  auditMore: boolean;
  /** Everything the hub reported; the entity fields above are this, narrowed to the workspace scope. */
  full: Entities;
  /** The workspace's project ids, or null for every project (the switcher in the top bar sets it). */
  scope: ReadonlySet<Id> | null;

  setAggregate: (s: AggregateState) => void;
  /** SSE 'ext': a module's update. Type 'state' replaces that module's slice. */
  applyExt: (e: SseEventMap['ext']) => void;
  /** Narrow the console to these projects; null shows every project. */
  setScope: (projectIds: readonly Id[] | null) => void;
  applyPatch: (p: StatePatch) => void;
  applyEvent: (e: FeedEvent) => void;
  applyEvents: (e: FeedEvent[]) => void;
  applyAudit: (a: AuditEntry) => void;
  /** Everything the stream delivered since the last frame, in one update (lib/batch.ts). */
  applyLive: (b: LiveBatch) => void;
  appendLog: (c: LogChunk) => void;
  /** This tab opened `streamId`: keep its chunks (and any that arrived first). */
  trackLog: (streamId: string) => void;
  /** Forget a stream: its buffer goes and late chunks for it are dropped. */
  clearLog: (streamId: string) => void;
  setConnection: (c: ConnectionStatus) => void;
  setPhase: (phase: LoadPhase, error?: UiError | null) => void;
  setMode: (m: DataMode) => void;
  /** Back to the empty loading state. */
  reset: () => void;
}

const initial = () => ({
  ...emptyEntities(),
  phase: 'loading' as LoadPhase,
  loadError: null,
  connection: 'connecting' as ConnectionStatus,
  mode: 'live' as DataMode,
  lastMessageAt: null,
  logs: {},
  openLogs: {},
  earlyLogs: {},
  auditMore: false,
  full: emptyEntities(),
});

/**
 * The scoped view of `full`, plus `full` itself (lib/scope.ts). With no scope the view is
 * `full` (the same objects), so All projects costs nothing extra.
 */
const scoper = createScoper();
const view = (full: Entities, scope: ReadonlySet<Id> | null) => ({ ...scoper(full, scope), full });

type ExtListener = (e: SseEventMap['ext']) => void;
const extListeners = new Set<ExtListener>();

/**
 * Hear every module (ext) event as it is applied, including the types the store does not keep
 * (a module's own, such as the assistant's 'conversation'). Returns the unsubscribe.
 */
export function onExtEvent(fn: ExtListener): () => void {
  extListeners.add(fn);
  return () => void extListeners.delete(fn);
}

function emitExt(list: readonly SseEventMap['ext'][]): void {
  if (!extListeners.size) return;
  for (const e of list) {
    for (const fn of extListeners) {
      try {
        fn(e);
      } catch {
        // One module's listener never breaks the stream for the others.
      }
    }
  }
}

/** How long chunks for a stream nobody has claimed yet are held. */
const EARLY_LOG_MS = 30_000;

function appendTo(prev: LogBuffer | undefined, c: LogChunk): LogBuffer {
  let text = (prev?.text ?? '') + c.text;
  if (text.length > LOG_LIMIT_CHARS) text = text.slice(text.length - LOG_LIMIT_CHARS);
  return { text, ended: !!c.end || !!prev?.ended, updatedAt: Date.now() };
}

export const useOrgus = create<OrgusStore>()((set) => ({
  ...initial(),
  scope: null,
  setAggregate: (s) =>
    set((st) => ({ ...view(fromAggregate(s, st.full), st.scope), auditMore: s.auditMore ?? false, phase: 'ready', loadError: null, lastMessageAt: Date.now() })),
  applyPatch: (p) => set((st) => ({ ...view(applyPatchTo(st.full, p), st.scope), lastMessageAt: Date.now() })),
  applyEvent: (e) =>
    set((st) => {
      const full = { ...st.full, feed: addFeedEvents(st.full.feed, [e], FEED_LIMIT) };
      return { full, feed: feedInScope(e, st.scope) ? addFeedEvents(st.feed, [e], FEED_LIMIT) : st.feed, lastMessageAt: Date.now() };
    }),
  applyEvents: (e) =>
    set((st) => {
      const full = { ...st.full, feed: addFeedEvents(st.full.feed, e, FEED_LIMIT) };
      return { full, feed: addFeedEvents(st.feed, st.scope ? e.filter((x) => feedInScope(x, st.scope)) : e, FEED_LIMIT), lastMessageAt: Date.now() };
    }),
  applyAudit: (a) =>
    set((st) => {
      const audit = addFeedEvents(st.full.audit, [a], AUDIT_LIMIT);
      return { full: { ...st.full, audit }, audit, lastMessageAt: Date.now() };
    }),
  applyExt: (e) => {
    set((st) => ({ ...view(withDeclaredNames({ ...st.full, ext: applyExtTo(st.full.ext, e) }), st.scope), lastMessageAt: Date.now() }));
    emitExt([e]);
  },
  setScope: (ids) =>
    set((st) => {
      const scope = ids ? new Set(ids) : null;
      return { scope, ...view(st.full, scope) };
    }),
  applyLive: (b) => {
    set((st) => {
      const patched = applyPatchesTo(st.full, b.patches);
      const full: Entities = {
        ...patched,
        feed: addFeedEvents(st.full.feed, b.events, FEED_LIMIT),
        audit: addFeedEvents(st.full.audit, b.audit, AUDIT_LIMIT),
        ext: b.ext.length ? b.ext.reduce(applyExtTo, st.full.ext) : st.full.ext,
      };
      return { ...view(b.ext.length ? withDeclaredNames(full) : full, st.scope), lastMessageAt: Date.now() };
    });
    if (b.ext.length) emitExt(b.ext);
  },
  appendLog: (c) =>
    set((st) => {
      if (st.openLogs[c.streamId]) return { logs: { ...st.logs, [c.streamId]: appendTo(st.logs[c.streamId], c) } };
      // Not (yet) ours: hold it briefly in case the open request is still on its way back.
      const cut = Date.now() - EARLY_LOG_MS;
      const earlyLogs: Record<string, LogBuffer> = {};
      for (const [id, b] of Object.entries(st.earlyLogs)) if (b.updatedAt > cut) earlyLogs[id] = b;
      earlyLogs[c.streamId] = appendTo(earlyLogs[c.streamId], c);
      return { earlyLogs };
    }),
  trackLog: (streamId) =>
    set((st) => {
      const early = st.earlyLogs[streamId];
      const earlyLogs = { ...st.earlyLogs };
      delete earlyLogs[streamId];
      return {
        openLogs: { ...st.openLogs, [streamId]: true },
        earlyLogs,
        ...(early && !st.logs[streamId] ? { logs: { ...st.logs, [streamId]: early } } : {}),
      };
    }),
  clearLog: (streamId) =>
    set((st) => {
      const logs = { ...st.logs };
      delete logs[streamId];
      const openLogs = { ...st.openLogs };
      delete openLogs[streamId];
      const earlyLogs = { ...st.earlyLogs };
      delete earlyLogs[streamId];
      return { logs, openLogs, earlyLogs };
    }),
  setConnection: (connection) => set({ connection }),
  setPhase: (phase, error = null) => set({ phase, loadError: error }),
  setMode: (mode) => set({ mode }),
  reset: () => set((st) => ({ ...initial(), scope: st.scope })),
}));

// ---------------------------------------------------------------------------
// Selectors: pure functions (usable anywhere) and hooks (re-render on change only)
// ---------------------------------------------------------------------------

export interface SessionFilter {
  status?: SessionStatus | SessionStatus[];
  projectId?: Id | null;
  machineId?: Id;
  checkoutId?: Id;
}

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name);

export function selectMachines(s: Entities): Machine[] {
  return Object.values(s.machines).sort((a, b) => Number(b.online) - Number(a.online) || byName(a, b));
}

export function selectProjects(s: Entities): Project[] {
  return Object.values(s.projects).sort(byName);
}

export function selectCheckouts(s: Entities, f: { projectId?: Id; machineId?: Id } = {}): Checkout[] {
  return Object.values(s.checkouts)
    .filter((c) => (f.projectId == null || c.projectId === f.projectId) && (f.machineId == null || c.machineId === f.machineId))
    .sort((a, b) => Number(a.kind === 'worktree') - Number(b.kind === 'worktree') || a.path.localeCompare(b.path));
}

/** The unfiltered, sorted list per sessions record: selectors run on every store change, the sort only when sessions change. */
const allSessionsCache = new WeakMap<Record<Id, Session>, Session[]>();

/** Sessions, most recently active first. */
export function selectSessions(s: Entities, f: SessionFilter = {}): Session[] {
  const unfiltered = f.status == null && f.projectId === undefined && f.machineId == null && f.checkoutId == null;
  if (unfiltered) {
    const hit = allSessionsCache.get(s.sessions);
    if (hit) return hit;
    const all = Object.values(s.sessions).sort((a, b) => b.lastActivity - a.lastActivity);
    allSessionsCache.set(s.sessions, all);
    return all;
  }
  const statuses = f.status == null ? null : Array.isArray(f.status) ? f.status : [f.status];
  return Object.values(s.sessions)
    .filter(
      (x) =>
        (!statuses || statuses.includes(x.status)) &&
        (f.projectId === undefined || x.projectId === f.projectId) &&
        (f.machineId == null || x.machineId === f.machineId) &&
        (f.checkoutId == null || x.checkoutId === f.checkoutId),
    )
    .sort((a, b) => b.lastActivity - a.lastActivity);
}

export function selectPipelines(s: Entities, projectId?: Id): Pipeline[] {
  return Object.values(s.pipelines)
    .filter((p) => projectId == null || p.projectId === projectId)
    .sort((a, b) => a.projectId.localeCompare(b.projectId) || a.machineId.localeCompare(b.machineId));
}

export function selectJobs(s: Entities, machineId?: Id): Job[] {
  return Object.values(s.jobs)
    .filter((j) => machineId == null || j.machineId === machineId)
    .sort((a, b) => b.startedAt - a.startedAt);
}

export interface LiveCounts {
  sessionsWorking: number;
  sessionsWaiting: number;
  sessionsIdle: number;
  subagentsRunning: number;
  workflowsRunning: number;
  /**
   * Running subagents plus the running agents of running workflows (a running workflow that
   * lists no agents yet counts as one). Equals the Agents page's Running count.
   */
  agentsRunning: number;
  /** Pipeline items queued or active, across every pipeline. */
  pipelineInFlight: number;
  pipelineFailedRecent: number;
  machinesOnline: number;
  machinesTotal: number;
  jobsRunning: number;
}

/**
 * What one session runs, remembered per session object: a patch replaces only the sessions
 * that changed, so recounting 10,000 sessions walks their workflow agents only for those.
 */
const runningCache = new WeakMap<Session, { subagents: number; workflows: number; agents: number }>();
function sessionRunning(x: Session): { subagents: number; workflows: number; agents: number } {
  let r = runningCache.get(x);
  if (!r) {
    r = { subagents: 0, workflows: 0, agents: runningUnits(x) };
    for (const a of x.subagents) if (a.status === 'running') r.subagents++;
    for (const w of x.workflows) if (w.status === 'running') r.workflows++;
    runningCache.set(x, r);
  }
  return r;
}

export function selectCounts(s: Entities): LiveCounts {
  const c: LiveCounts = {
    sessionsWorking: 0,
    sessionsWaiting: 0,
    sessionsIdle: 0,
    subagentsRunning: 0,
    workflowsRunning: 0,
    agentsRunning: 0,
    pipelineInFlight: 0,
    pipelineFailedRecent: 0,
    machinesOnline: 0,
    machinesTotal: 0,
    jobsRunning: 0,
  };
  for (const x of Object.values(s.sessions)) {
    if (x.status === 'working') c.sessionsWorking++;
    else if (x.status === 'waiting') c.sessionsWaiting++;
    else if (x.status === 'idle') c.sessionsIdle++;
    const r = sessionRunning(x);
    c.subagentsRunning += r.subagents;
    c.workflowsRunning += r.workflows;
    c.agentsRunning += r.agents;
  }
  for (const p of Object.values(s.pipelines)) {
    for (const i of p.items) if (i.state === 'queued' || i.state === 'active') c.pipelineInFlight++;
    for (const i of p.recent) if (i.state === 'failed') c.pipelineFailedRecent++;
  }
  for (const m of Object.values(s.machines)) {
    c.machinesTotal++;
    if (m.online) c.machinesOnline++;
  }
  for (const j of Object.values(s.jobs)) if (j.state === 'running') c.jobsRunning++;
  return c;
}

/**
 * A selector that recomputes only when the store fields it reads (`inputs`) change identity,
 * and keeps its previous result when the new one is shallowly equal, so consumers do not
 * re-render. At 10,000 sessions, sorting them on every store update (a feed event, a log
 * chunk, the connection status) was most of the console's time.
 */
export function memoSelector<R>(inputs: (s: Entities) => readonly unknown[], compute: (s: Entities) => R): (s: Entities) => R {
  let lastIn: readonly unknown[] | null = null;
  let lastOut: R;
  return (s) => {
    const ins = inputs(s);
    const prev = lastIn;
    if (prev && ins.length === prev.length && ins.every((x, i) => x === prev[i])) return lastOut;
    const out = compute(s);
    if (!prev || !shallow(out, lastOut)) lastOut = out;
    lastIn = ins;
    return lastOut;
  };
}

/** One memoised selector per distinct argument, kept for the component's life. */
function useMemoSelector<R>(key: string, inputs: (s: Entities) => readonly unknown[], compute: (s: Entities) => R): R {
  // `key` stands for everything `compute` reads besides the store.
  const sel = useMemo(() => memoSelector(inputs, compute), [key]);
  return useOrgus(sel);
}

const machinesSel = memoSelector((s) => [s.machines], selectMachines);
const projectsSel = memoSelector((s) => [s.projects], selectProjects);
const suggestionsSel = memoSelector((s) => [s.suggestions], (s) => Object.values(s.suggestions));
const allSessionsSel = memoSelector((s) => [s.sessions], (s) => Object.values(s.sessions));
const countsSel = memoSelector((s) => [s.sessions, s.pipelines, s.machines, s.jobs], selectCounts);

// Hooks. Each returns stable references while the underlying data is unchanged.

export const useMachines = (): Machine[] => useOrgus(machinesSel);
export const useMachine = (id: Id | null | undefined): Machine | undefined => useOrgus((s) => (id ? s.machines[id] : undefined));
export const useMachineHistory = (id: Id): MachineSample[] | undefined => useOrgus((s) => s.machineHistory[id]);
export const useProjects = (): Project[] => useOrgus(projectsSel);
export const useProject = (id: Id | null | undefined): Project | undefined => useOrgus((s) => (id ? s.projects[id] : undefined));
export const useCheckouts = (f: { projectId?: Id; machineId?: Id } = {}): Checkout[] =>
  useMemoSelector(`${f.projectId ?? ''}\u0000${f.machineId ?? ''}`, (s) => [s.checkouts], (s) => selectCheckouts(s, f));
export const useCheckout = (id: Id | null | undefined): Checkout | undefined => useOrgus((s) => (id ? s.checkouts[id] : undefined));
/** Every session, in no particular order: for views that group and sort themselves (cheaper than useSessions at scale). */
export const useAllSessions = (): Session[] => useOrgus(allSessionsSel);
/**
 * The memo key of a session filter. projectId undefined means every project and null means
 * no project, so they must not share a key (JSON.stringify writes both as null).
 */
export const sessionFilterKey = (f: SessionFilter): string =>
  JSON.stringify([f.status ?? null, f.projectId === undefined ? 0 : f.projectId, f.machineId ?? null, f.checkoutId ?? null]);
export const useSessions = (f: SessionFilter = {}): Session[] =>
  useMemoSelector(sessionFilterKey(f), (s) => [s.sessions], (s) => selectSessions(s, f));
export const useSession = (id: Id | null | undefined): Session | undefined => useOrgus((s) => (id ? s.sessions[id] : undefined));
export const usePipelines = (projectId?: Id): Pipeline[] =>
  useMemoSelector(projectId ?? '', (s) => [s.pipelines], (s) => selectPipelines(s, projectId));
export const usePipeline = (key: string | null | undefined): Pipeline | undefined =>
  useOrgus((s) => (key ? s.pipelines[key] : undefined));
export const useJobs = (machineId?: Id): Job[] => useMemoSelector(machineId ?? '', (s) => [s.jobs], (s) => selectJobs(s, machineId));
export const useJob = (id: Id | null | undefined): Job | undefined => useOrgus((s) => (id ? s.jobs[id] : undefined));
export const useSuggestions = (): ProjectSuggestion[] => useOrgus(suggestionsSel);
/** Newest first. */
export const useFeed = (limit = FEED_LIMIT): FeedEvent[] =>
  useOrgus(useShallow((s) => (limit >= s.feed.length ? s.feed : s.feed.slice(0, limit))));
/** Newest first. */
export const useAudit = (): AuditEntry[] => useOrgus((s) => s.audit);
/** A feature module's slice of the state (state.ext[name]), or undefined before it reports. */
export const useExt = <T,>(name: string): T | undefined => useOrgus((s) => s.ext[name] as T | undefined);
export const useCounts = (): LiveCounts => useOrgus(countsSel);
export const useLog = (streamId: string | null | undefined): LogBuffer | undefined =>
  useOrgus((s) => (streamId ? s.logs[streamId] : undefined));
export const useConnection = () =>
  useOrgus(
    useShallow((s) => ({ connection: s.connection, mode: s.mode, phase: s.phase, loadError: s.loadError, lastMessageAt: s.lastMessageAt })),
  );
